/* ══════════════════════════════════════════════════════════════
   doctorViewUtils.js
   API helpers · data processors · vital config
══════════════════════════════════════════════════════════════ */

/* ─── API helpers ──────────────────────────────────────────────── */
const BASE = process.env.REACT_APP_API_BASE_URL;
const tok  = () => localStorage.getItem("token");
const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${tok()}` });
export const fmtDate = (d) => d.toISOString().split("T")[0];

export async function searchPatientByPhone(phone) {
  const res  = await fetch(`${BASE}/api/program/listpatients?view=task_list`, { headers: hdrs() });
  const json = await res.json();
  const list = Array.isArray(json.data) ? json.data : [];
  return list.find(p =>
    String(p.user_profile?.phone_no || "").replace(/\D/g, "") ===
    String(phone).replace(/\D/g, "")
  ) || null;
}

export async function fetchFullProgram(patientId) {
  const res  = await fetch(
    `${BASE}/api/program/listpatients?view=task_list&patient_id=${patientId}`,
    { headers: hdrs() }
  );
  const json = await res.json();
  if (json.data && typeof json.data === "object" && !Array.isArray(json.data)) return json.data;
  return null;
}

export async function fetchTrendsAllPages(ownerId) {
  const end = new Date(), start = new Date();
  start.setDate(start.getDate() - 90);
  const startDate = fmtDate(start), endDate = fmtDate(end);
  const PAGE_SIZE = 250;

  async function getPage(page) {
    const p = new URLSearchParams({
      trend_type: "vital_trends", patient_owner_id: ownerId,
      start_date: startDate, end_date: endDate, page, page_size: PAGE_SIZE
    });
    const r = await fetch(`${BASE}/api/patients/health/trends?${p}`, { headers: hdrs() });
    return r.json();
  }

  const first      = await getPage(1);
  const result     = first?.data?.result;
  const totalPages = result?.wearable_trends?.total_pages ?? 1;
  const all        = [...(result?.wearable_trends?.data || [])];

  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) => getPage(i + 2))
    );
    rest.forEach(d => all.push(...(d?.data?.result?.wearable_trends?.data || [])));
  }

  return { wearable: all, cgm: result?.cgm_trends || [], patient: result?.patient };
}

/* ─── Timestamp normaliser ─────────────────────────────────────────
   jcband uses ms (e.g. 1775611020000)
   qring  uses bare Unix seconds (e.g. 1775067600)
   Always return milliseconds.
──────────────────────────────────────────────────────────────── */
function toMs(ts) {
  if (!ts) return 0;
  return ts < 1e12 ? ts * 1000 : ts;
}

/* ─── Wearable data processor ──────────────────────────────────────
   Processes all vitals EXCEPT sleep.
   Sleep is passed as raw items directly to SleepChart.
   This keeps sleep data lossless — no intermediate transformation.
──────────────────────────────────────────────────────────────── */
export function processWearable(data) {
  const out = {
    heart_rate: [],
    spo2:       [],
    hrv:        [],
    stress:     [],
    activity:   [],
    /* sleep is intentionally NOT processed here — use extractSleepItems() instead */
    sleep:      [],
  };

  data.forEach(item => {
    const type = item.type;
    const tsMs = toMs(item.ts);
    const d    = new Date(tsMs);

    const dateLabel = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    const dateKey   = fmtDate(d);

    if (type === "heart_rate" && item.heart_rate?.vital_graph?.length) {
      item.heart_rate.vital_graph.forEach(p =>
        out.heart_rate.push({ date: dateLabel, dateKey, ts: toMs(p.ts), value: p.value })
      );
    }
    else if (type === "spo2" && item.spo2?.vital_graph?.length) {
      item.spo2.vital_graph.forEach(p =>
        out.spo2.push({ date: dateLabel, dateKey, ts: toMs(p.ts), value: p.value })
      );
    }
    else if (type === "hrv" && item.hrv?.vital_graph?.length) {
      item.hrv.vital_graph.forEach(p =>
        out.hrv.push({ date: dateLabel, dateKey, ts: toMs(p.ts), value: p.value })
      );
    }
    else if (type === "stress" && item.stress?.vital_graph?.length) {
      item.stress.vital_graph.forEach(p =>
        out.stress.push({ date: dateLabel, dateKey, ts: toMs(p.ts), value: p.value })
      );
    }
    else if (type === "activity" && item.activity?.step_graph?.length) {
      item.activity.step_graph.forEach(p =>
        out.activity.push({
          date: dateLabel, dateKey, ts: toMs(p.ts),
          steps: p.steps, calories: item.activity.calorie
        })
      );
    }
    /* ── sleep is skipped here on purpose ── */
  });

  /* Sort each array by timestamp */
  Object.keys(out).forEach(k =>
    out[k].sort((a, b) => (a.ts || 0) - (b.ts || 0))
  );

  return out;
}

/* ─── Extract raw sleep items ──────────────────────────────────────
   Returns the raw wearable items where type === "sleep".
   Pass this array directly to <SleepChart rawSleepItems={...} />.
   SleepChart reads item.sleep.sleep_graph[], total_sleep_duration,
   deep_duration, shallow_duration, rapid_duration from each item.
──────────────────────────────────────────────────────────────── */
export function extractSleepItems(wearableData) {
  if (!Array.isArray(wearableData)) return [];
  return wearableData.filter(
    item => item.type === "sleep" && item.sleep?.sleep_graph?.length > 0
  );
}

/* ─── Vital config ──────────────────────────────────────────────── */
export const VITALS = [
  { key: "heart_rate", label: "❤️ Heart Rate",  unit: "bpm",   color: "#ef4444", dataKey: "value", refLow: 60,  refHigh: 100, normal: "60–100 bpm" },
  { key: "spo2",       label: "💧 SpO₂",         unit: "%",     color: "#3b82f6", dataKey: "value", refLow: 95,  refHigh: 100, normal: "95–100%" },
  { key: "hrv",        label: "📈 HRV",           unit: "ms",    color: "#8b5cf6", dataKey: "value", refLow: 20,  refHigh: 120, normal: "20–120 ms" },
  { key: "stress",     label: "😓 Stress",        unit: "/100",  color: "#f59e0b", dataKey: "value", refLow: 0,   refHigh: 50,  normal: "Below 50 is calm" },
  { key: "activity",   label: "🏃 Daily Steps",   unit: "steps", color: "#10b981", dataKey: "steps", refLow: 0,   refHigh: 8000, normal: "Goal: 8,000 steps" },
  { key: "sleep",      label: "🌙 Sleep",         unit: "hrs",   color: "#6366f1", dataKey: "value" },
];